import { useState } from "react";
import { VerifiedBadge } from "@/components/moderation/VerifiedBadge";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Users, ScrollText, Mail } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function CommunityCard({ c }) {
  return (
    <Link to={`/c/${c.slug}`} className="panel group block overflow-hidden transition-colors duration-200 hover:border-amber-500/60" data-testid={`community-card-${c.slug}`}>
      <div className="h-20 bg-gradient-to-r from-[#132036] to-[#1d2b45]" style={c.banner_url ? { backgroundImage: `url(${assetUrl(c.banner_url)})`, backgroundSize: "cover" } : undefined} />
      <div className="p-4">
        <div className="flex items-center gap-3">
          {c.logo_url ? <img src={assetUrl(c.logo_url)} alt="" className="-mt-10 h-12 w-12 rounded-full border-2 border-amber-600 object-cover" /> : <span className="-mt-10 flex h-12 w-12 items-center justify-center rounded-full border-2 border-amber-600 bg-[#0E1424] font-heading text-amber-200">{c.name[0]}</span>}
          <h3 className="font-heading text-lg text-amber-100 group-hover:text-aether-300">{c.name} <VerifiedBadge kind="community" verified={c.verified} compact /></h3>
        </div>
        <p className="mt-2 line-clamp-2 text-sm text-slate-400">{c.description || "No description yet."}</p>
        <div className="mt-3 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-wider">
          <span className="rounded-full border border-emerald-700/60 px-2 py-0.5 text-emerald-300" data-testid="community-approved-badge">Approved</span>
          {c.verified && <span className="rounded-full border border-aether-500 px-2 py-0.5 text-aether-300">Verified</span>}
          <span className="rounded-full border border-[#2A3447] px-2 py-0.5 text-slate-400"><Users className="mr-1 inline h-3 w-3" />{c.member_count}</span>
          <span className="rounded-full border border-[#2A3447] px-2 py-0.5 text-slate-400">{c.join_policy.replace("_", " ")}</span>
          {c.my_role && <span className="rounded-full border border-amber-700 px-2 py-0.5 text-amber-300">{c.my_role.replace("_", " ")}</span>}
        </div>
      </div>
    </Link>
  );
}

function Invitations() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["me", "invitations"], queryFn: () => api.get("/me/invitations").then((r) => r.data.items) });
  const answer = async (id, a) => {
    try { await api.post(`/invitations/${id}/${a}`); toast.success(a === "accept" ? "Welcome aboard" : "Invitation declined"); qc.invalidateQueries({ queryKey: ["me"] }); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!data?.length) return null;
  return (
    <div className="panel space-y-2 p-4" data-testid="my-invitations">
      <p className="eyebrow"><Mail className="mr-1 inline h-3.5 w-3.5" />Invitations</p>
      {data.map((i) => (
        <div key={i.invitation_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`invitation-${i.invitation_id}`}>
          <span>{i.community?.name}</span>
          <span className="flex gap-2">
            <button className="btn-gold px-3 py-1 text-xs" onClick={() => answer(i.invitation_id, "accept")} data-testid={`invitation-accept-${i.invitation_id}`}>Accept</button>
            <button className="btn-outline px-3 py-1 text-xs" onClick={() => answer(i.invitation_id, "decline")} data-testid={`invitation-decline-${i.invitation_id}`}>Decline</button>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Communities() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [accepting, setAccepting] = useState("");
  const [events, setEvents] = useState(false);
  const params = { q: q || undefined, accepting: accepting || undefined, has_events: events || undefined };
  const { data, isLoading } = useQuery({ queryKey: ["communities", params], queryFn: () => api.get("/communities", { params }).then((r) => r.data) });
  const { data: mine } = useQuery({ queryKey: ["me", "communities"], enabled: !!user, queryFn: () => api.get("/me/communities").then((r) => r.data.items) });
  return (
    <div className="container-x py-12" data-testid="communities-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Communities</p>
          <h1 className="font-display text-4xl text-amber-100 sm:text-5xl">Guild Halls of the Multiverse</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">Approved MTG communities. New communities apply first and are listed once staff approve them. Approved is not the same as Verified: verification arrives in Phase 5.</p>
        </div>
        {user && <Link to="/communities/applications" className="btn-gold" data-testid="communities-apply-button"><ScrollText className="h-4 w-4" />Start a community</Link>}
      </div>
      {user && <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_320px]">
        <div>{mine?.length > 0 && <><p className="eyebrow mb-3">My communities</p><div className="grid gap-4 sm:grid-cols-2" data-testid="my-communities">{mine.map((c) => <CommunityCard key={c.community_id} c={c} />)}</div></>}</div>
        <Invitations />
      </div>}
      <div className="mt-10 flex flex-wrap gap-3">
        <label className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder="Search communities" value={q} onChange={(e) => setQ(e.target.value)} data-testid="communities-search-input" /></label>
        <select className="field w-48" value={accepting} onChange={(e) => setAccepting(e.target.value)} data-testid="communities-accepting-filter">
          <option value="">Any membership</option><option value="true">Accepting members</option><option value="false">Invite only</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={events} onChange={(e) => setEvents(e.target.checked)} data-testid="communities-events-filter" />Upcoming events</label>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="communities-directory">
        {isLoading ? <p className="text-slate-500">Loading…</p> : data?.items.length ? data.items.map((c) => <CommunityCard key={c.community_id} c={c} />)
          : <p className="text-slate-500" data-testid="communities-empty">No approved communities match yet.</p>}
      </div>
    </div>
  );
}
